import { useEffect, useState } from "react";
import { Form, Button, Modal, message, Alert } from "antd";
import ViewingForm from "./ViewingForm";
import DynamicFormFields from "./DynamicFormFields";
import useAxios from "../../hooks/useAxios";
import usePdfBlob from "../../hooks/usePdfBlob";
import PdfPreviewer from "../PdfPreviewer";

const LivePdfReviewContent = ({ endpoint, params }) => {
  const { pdfBlobUrl, loading, error } = usePdfBlob({ endpoint, params });

  if (error) {
    return <Alert type="error" message={error} showIcon />;
  }

  return <PdfPreviewer pdfBlobUrl={pdfBlobUrl} loading={loading || !pdfBlobUrl} height="600px" />;
};

const PvForm = ({
  isService,
  formFields,
  initialValues,
  viewingDataConfig,
  reviewPdfEndpoint,
  apiConfig,
  onSubmitted,
  submitDisabled = false,
  previousSubmissions,
  submitTransform,
}) => {
  const [form] = Form.useForm();
  const { loading, fetchData } = useAxios();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [pendingSubmission, setPendingSubmission] = useState(null);

  useEffect(() => {
    if (initialValues) {
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [initialValues, form]);

  const handleRadioChange = (fieldName, e) => {
    if (e.target.value === "disable") {
      form.setFieldsValue({ [fieldName]: "" });
    }
  };

  const handleSubmit = async (values) => {
    if (submitDisabled) {
      return;
    }

    if (isService === true) {
      Modal.confirm({
        title: "Are you sure?",
        okText: "Submit",
        cancelText: "Cancel",
        onOk: () => {
          onSubmitted(values);
          form.resetFields();
        },
      });
    } else {
      const reviewData = {
        ...values,
        ...(apiConfig?.additionalData || {}),
      };
      const requestData = {
        ...(submitTransform ? submitTransform(values) : values),
        ...(apiConfig?.additionalData || {}),
      };

      // A PDF preview is wide, tall content — it doesn't fit Modal.confirm's
      // icon + indented-text layout, so it gets its own plain Modal instead.
      if (reviewPdfEndpoint) {
        setPendingSubmission({ reviewData, requestData });
        setReviewOpen(true);
        return;
      }

      Modal.confirm({
        title: "Please review your details before submitting.",
        width: "60%",
        content: (
          <ViewingForm
            viewingDataConfig={viewingDataConfig}
            data={reviewData}
          />
        ),
        okText: "Submit",
        cancelText: "Cancel",
        onOk: () =>
          new Promise((resolve, reject) => {
            fetchData({
              url: apiConfig?.url,
              method: apiConfig?.method,
              data: requestData,
            })
              .then(() => {
                onSubmitted?.();
                form.resetFields();
                resolve();
              })
              .catch((error) => {
                const msg =
                  error?.response?.data?.message ||
                  error?.message ||
                  "Submission failed. Please try again.";
                message.error(msg);
                reject();
              });
          }),
      });
    }
  };

  const closeReviewModal = () => {
    setReviewOpen(false);
    setPendingSubmission(null);
  };

  const handleConfirmPdfSubmit = () => {
    if (!pendingSubmission) return;
    fetchData({
      url: apiConfig?.url,
      method: apiConfig?.method,
      data: pendingSubmission.requestData,
    })
      .then(() => {
        onSubmitted?.();
        form.resetFields();
        closeReviewModal();
      })
      .catch((error) => {
        const msg =
          error?.response?.data?.message ||
          error?.message ||
          "Submission failed. Please try again.";
        message.error(msg);
      });
  };

  return (
    <Form
      form={form}
      layout="horizontal"
      initialValues={initialValues}
      onFinish={handleSubmit}
    >
      <DynamicFormFields
        fields={formFields}
        form={form}
        onRadioChange={handleRadioChange}
        previousData={previousSubmissions}
      />
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          loading={loading}
          disabled={submitDisabled}
        >
          Review
        </Button>
      </Form.Item>

      {reviewPdfEndpoint && (
        <Modal
          open={reviewOpen}
          title="Please review your details before submitting."
          width={850}
          onCancel={closeReviewModal}
          destroyOnClose
          footer={[
            <Button key="cancel" onClick={closeReviewModal}>
              Cancel
            </Button>,
            <Button
              key="submit"
              type="primary"
              loading={loading}
              onClick={handleConfirmPdfSubmit}
            >
              Submit
            </Button>,
          ]}
        >
          {pendingSubmission && (
            <LivePdfReviewContent
              endpoint={reviewPdfEndpoint}
              params={{
                employee_id: pendingSubmission.reviewData.employee_id,
                academic_term: pendingSubmission.reviewData.academic_term,
                position_id: pendingSubmission.reviewData.position_id,
                status_of_appointment: pendingSubmission.reviewData.status_of_appointment,
              }}
            />
          )}
        </Modal>
      )}
    </Form>
  );
};

export default PvForm;
